import {
  ArrowsRightLeftIcon,
  Button,
  Divider,
  Flex,
  Panel,
  RadioCardOption,
  RadioCards,
  Text,
  TextField,
} from 'components'
import { BrowsersIcon } from 'components/icons'
import { isEqual } from 'lodash-es'
import { useEffect, useState } from 'react'
import { ExternalClientAppType, IExternalClient, OmitDBProps } from 'types'
import { createExternalClient, updateExternalClient, useBreakpoint } from 'utils'
import ApplicationCredentials from './ApplicationCredentials'
import ScopeEditor from './ScopeEditor'

interface Props {
  workspaceId: string
  onClose: () => void
  app?: IExternalClient | null
}

interface AppCreds {
  clientId: string
  clientSecret: string
}

export default function ApplicationInfo({ workspaceId, onClose, app }: Props) {
  const [appType, setAppType] = useState<ExternalClientAppType>('m2m')
  const [draft, setDraft] = useState<Partial<IExternalClient | undefined>>(
    app || { type: appType, scopes: [] }
  )
  const [saveEnabled, setSaveEnabled] = useState<boolean>(false)
  const [appCreds, setAppCreds] = useState<AppCreds>()
  const [saveLoading, setSaveLoading] = useState<boolean>(false)
  const isMobile = useBreakpoint() < 2
  const dirty = !isEqual(app, draft)

  useEffect(() => {
    if (draft?.scopes?.length! > 0 && draft?.title) setSaveEnabled(true)
    else setSaveEnabled(false)
  }, [draft])

  function update(body: Partial<IExternalClient>) {
    setDraft(s => ({ ...s!, ...body }))
  }

  async function handleSave() {
    if (draft) {
      setSaveLoading(true)
      const appDetails: Omit<OmitDBProps<IExternalClient>, 'clientSecretHash'> = {
        scopes: draft.scopes!,
        status: 'initialized',
        title: draft.title!,
        type: draft.type!,
        workspaceId: workspaceId,
      }
      if (app) {
        await updateExternalClient({ id: app.id, ...appDetails })
        onClose()
      } else {
        const createdApp = (await createExternalClient(appDetails)).data
        setAppCreds({
          clientId: createdApp.id,
          clientSecret: createdApp.secret,
        })
      }
      setSaveLoading(false)
    }
  }

  function handleCancel() {
    setDraft(undefined)
    onClose()
  }

  if (appCreds)
    return (
      <ApplicationCredentials
        appTitle={draft?.title || ''}
        appType={draft?.type!}
        clientId={appCreds.clientId}
        clientSecret={appCreds.clientSecret}
        onClose={onClose}
      />
    )

  return (
    <Panel p={7} gap={5}>
      <Text textStyle="bodyMedium">{app ? 'Update' : 'Create'} Application</Text>

      <Flex gap={5} direction={isMobile ? 'column' : 'row'}>
        <RadioCards
          value={appType}
          options={appOptions}
          onChange={setAppType}
          isVertical={!isMobile}
        />
        {!isMobile && <Divider orientation="vertical" />}

        <Flex direction="column" gap={7} w="100%" maxW="600px">
          <TextField
            label="Application Name"
            placeholder="Enter name..."
            value={draft?.title || ''}
            onChange={e => update({ title: e.target.value })}
          />
          <ScopeEditor scopes={draft?.scopes} onChange={update} />
          <Flex gap={3} align="center" justify={isMobile ? 'flex-start' : 'flex-end'}>
            <Button variant="outline" onClick={handleCancel} disabled={!dirty}>
              Cancel
            </Button>
            <Button
              palette="primary"
              disabled={!saveEnabled}
              loading={saveLoading}
              onClick={dirty ? handleSave : handleCancel}
            >
              Done
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Panel>
  )
}
const appOptions: RadioCardOption[] = [
  {
    value: 'm2m',
    label: 'Machine to Machine',
    description: 'Enables direct data exchange between Momentify and your system.',
    icon: <ArrowsRightLeftIcon color="primary.500" fontSize="60px" />,
  },
  {
    value: 'web',
    label: 'Web Application',
    description: 'Connect Momentify to external services or clients',
    isDisabled: true,
    icon: <BrowsersIcon fontSize="60px" />,
  },
]
